import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@klaro7/ui';

import { PasswordToggleButton } from '@/components/PasswordInput';
import { resetPassword } from '@/features/auth/services/auth.service';

function validatePassword(pw: string): string | null {
  if (pw.length < 8) return 'La password deve contenere almeno 8 caratteri.';
  if (!/[a-z]/.test(pw)) return 'La password deve contenere almeno una lettera minuscola.';
  if (!/[A-Z]/.test(pw)) return 'La password deve contenere almeno una lettera maiuscola.';
  if (!/[0-9]/.test(pw)) return 'La password deve contenere almeno un numero.';
  return null;
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const pwError = validatePassword(newPassword);
    if (pwError) {
      setError(pwError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Le password non coincidono.');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword({ token, newPassword, confirmPassword });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset fallito. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    'flex h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2 text-sm text-white placeholder:text-white/30 transition-colors duration-200 focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20';

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4"
      style={{
        background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(24,153,170,0.15) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(118,185,185,0.08) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src="/images/logo-clean.png" alt="Klaro7" className="h-14 drop-shadow-lg" />
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          {!token ? (
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight text-white">Link non valido</h1>
              <p className="mt-3 text-sm text-white/60">
                Il link di reset è mancante o malformato. Richiedine uno nuovo.
              </p>
              <Link
                to="/forgot-password"
                className="mt-6 inline-block text-sm font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline"
              >
                Richiedi nuovo link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight text-white">Password aggiornata</h1>
              <p className="mt-3 text-sm text-white/60">
                La tua password è stata reimpostata con successo. Ora puoi accedere con le nuove credenziali.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block rounded-lg px-5 py-2.5 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #1899AA 0%, #126f7d 100%)' }}
              >
                Vai al login
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-semibold tracking-tight text-white">Nuova password</h1>
                <p className="mt-1.5 text-sm text-white/60">Scegli una nuova password per il tuo account</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <Alert variant="error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-1.5">
                  <label htmlFor="new-password" className="block text-sm font-medium text-white/70">
                    Nuova password
                  </label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Almeno 8 caratteri"
                      required
                      autoComplete="new-password"
                      className={`${inputClass} pr-10`}
                    />
                    <PasswordToggleButton
                      visible={showNewPassword}
                      onToggle={() => setShowNewPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-white/40 transition-colors hover:text-white/70"
                    />
                  </div>
                  <p className="text-xs text-white/40">
                    Min. 8 caratteri, con maiuscola, minuscola e numero.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-white/70">
                    Conferma password
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Ripeti la password"
                      required
                      autoComplete="new-password"
                      className={`${inputClass} pr-10`}
                    />
                    <PasswordToggleButton
                      visible={showConfirmPassword}
                      onToggle={() => setShowConfirmPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-white/40 transition-colors hover:text-white/70"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-brand-teal/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2 focus-visible:ring-offset-[#231F55] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #1899AA 0%, #126f7d 100%)' }}
                >
                  {isLoading ? (
                    <>
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-sm">Aggiornamento...</span>
                    </>
                  ) : (
                    <span className="text-sm">Reimposta password</span>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
